import { z } from 'zod';
import { requiredString, timestampedDocSchema } from '../utils/basicSchemas/document.z.schema';
import { mongoId, mongoIdSchema } from '../utils/basicSchemas/_id.z.schema';
import { ZSchema } from '../utils/zodSchema';


export const MAX_GALLERY_TITLE_LENGTH = 60;
export const MAX_GALLERY_DESC_LENGTH = 500;

const TITLE_REGEX = new RegExp(`^(?=.*[a-z])[ 0-9a-zA-Z]{1,${MAX_GALLERY_TITLE_LENGTH}}$`, 'i');
const DESC_REGEX = new RegExp(`^.{0,${MAX_GALLERY_DESC_LENGTH}}$`);


const gallerySchemaFull = z.object({
    title: requiredString("Gallery Title"),
    description: z.string().optional(),
    creator: mongoId("Gallery creator"),
    collaborators: z.array(mongoIdSchema),
    posts: z.array(mongoIdSchema),
    coverImageUrl: z.string().optional(),
    visible: z.enum(["everyone", "followers", "talents", "private"]).default("everyone"),
    talents: z.array(mongoIdSchema),
    followers: z.array(mongoIdSchema),
    subcategories: z.array(mongoIdSchema),
});


const gallerySchemaDoc = timestampedDocSchema.merge(gallerySchemaFull);

// Infer and Export types
type GallerySchemaType = z.infer<typeof gallerySchemaFull>;
type GallerySchemaDocType = z.infer<typeof gallerySchemaDoc>;


const gallerySchemaTitle = z.string()
    .min(1, "Gallery title must be at least one character")
    .max(MAX_GALLERY_TITLE_LENGTH,
        `Gallery title must be at most ${MAX_GALLERY_TITLE_LENGTH} characters`)
    .regex(TITLE_REGEX, "Gallery title is invalid");

const gallerySchemaDescription = z.string()
    .max(MAX_GALLERY_DESC_LENGTH,
        `Gallery description must be at most ${MAX_GALLERY_DESC_LENGTH} characters`)
    .regex(DESC_REGEX, "Gallery description is invalid").optional();


const gallerySchema = z.object({
    title: gallerySchemaTitle,
    description: gallerySchemaDescription
});

const galleryUpdateSchema = z.object({
    updates: z.object({
        _id: mongoId("Gallery id"),
        title: gallerySchemaTitle,
        description: gallerySchemaDescription,
        collaborators: z.array(mongoIdSchema).optional(),
        visible: z.enum(["everyone", "followers", "talents", "private"]).optional(),
        coverImageUrl: z.string().nullable().optional(),
    })
});

const galleryIdSchema = z.object({
    id: mongoId("Gallery id"),
});

// Validators 
const galleryIdValidator = new ZSchema({ paramSchema: galleryIdSchema });
const galleryValidator = new ZSchema({ bodySchema: gallerySchema });
const galleryUpdateValidator = new ZSchema({ bodySchema: galleryUpdateSchema });

export { galleryValidator, galleryIdValidator, galleryUpdateValidator };
export { gallerySchemaFull, gallerySchemaDoc };
export { GallerySchemaType, GallerySchemaDocType };
